/**
 * @fileoverview Pure WebSocket message handlers for the Chat page.
 * @module logic/chat/websocketHandlers
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  uploadFileToS3,
  extractMessageTimestamp,
  maybeMarkChatAsRead,
} from "./index.js";

/** Applies file-upload metadata to the matching chat in the inbox list. */
export const applyFileMessageToChat = (chat, data, currentUserId) => {
  if (chat.PK !== data.chatId) {
    return chat;
  }
  return {
    ...chat,
    lastMessage: data.fileData.fileName,
    lastMessageCreatedAt: data.timestamp,
    lastMessageSenderUserId: currentUserId,
    lastMessageRecipientUserId: data.chatId,
    lastMessageSeen: true,
  };
};

/** Applies a text message preview to the matching chat in the inbox list. */
export const applyTextMessageToChat = (
  chat,
  data,
  currentUserId,
  shouldMarkSeen,
) => {
  if (chat.PK !== data.chatId) {
    return chat;
  }
  return {
    ...chat,
    lastMessage: data.message,
    lastMessageCreatedAt: data.timestamp,
    lastMessageSenderUserId: data.senderUserId,
    lastMessageRecipientUserId: currentUserId,
    lastMessageSeen: shouldMarkSeen,
  };
};

const getReadContext = (deps, data) => {
  const currentUserId = localStorage.getItem("userId");
  const isIncoming = data.senderUserId !== currentUserId;
  const isActiveChat = deps.selectedChatRef.current?.PK === data.chatId;
  const isVisible =
    document.visibilityState === "visible" && deps.isUserActiveRef.current;
  const shouldMarkSeen = isIncoming ? isActiveChat && isVisible : true;

  return {
    currentUserId,
    isIncoming,
    isActiveChat,
    isVisible,
    shouldMarkSeen,
  };
};

const maybePersistReadStatus = (deps, data, readContext) => {
  const { isIncoming, isActiveChat, isVisible } = readContext;
  if (isIncoming && isActiveChat && isVisible) {
    maybeMarkChatAsRead(
      data.chatId,
      extractMessageTimestamp(data),
      deps.navigate,
      deps.isUserActiveRef,
      deps.lastSeenMessageTimestampByChatRef,
    );
  }
};

/** Handles incoming text chat messages. */
export const handleNewMessage = async (data, deps) => {
  logger.info("New message received:", { data });
  deps.setChatMessages((prevMessages) => [...prevMessages, data]);

  const readContext = getReadContext(deps, data);
  logger.info("NEW_MESSAGE mark-as-read check", {
    data: {
      isIncoming: readContext.isIncoming,
      isActiveChat: readContext.isActiveChat,
      isVisible: readContext.isVisible,
      chatId: data.chatId,
      timestamp: data.timestamp,
    },
  });

  deps.setChats((prevChats) =>
    prevChats.map((chat) =>
      applyTextMessageToChat(
        chat,
        data,
        readContext.currentUserId,
        readContext.shouldMarkSeen,
      ),
    ),
  );

  maybePersistReadStatus(deps, data, readContext);
};

const handleFilePresignedUpload = async (data, deps) => {
  if (
    !data.fileData.fileName ||
    !data.fileData.fileKey ||
    !data.fileData.fileExpiresAt
  ) {
    logger.error("Missing required file data fields:", { data: data.fileData });
    return;
  }

  const storedFile = deps.pendingFileUploads.current.get(data.messageId);
  if (!storedFile) {
    logger.error("No stored file found for message ID:", {
      data: data.messageId,
    });
    return;
  }

  const uploadSuccess = await uploadFileToS3(
    storedFile,
    data.fileData.presignedUrl,
  );

  if (!uploadSuccess) {
    logger.error("Failed to upload file to S3");
    deps.pendingFileUploads.current.delete(data.messageId);
    deps.setIsFileUploading(false);
    return;
  }

  logger.info("File uploaded successfully to S3");
  deps.pendingFileUploads.current.delete(data.messageId);

  const finalFileMessage = {
    type: "FILE_MESSAGE",
    messageId: data.messageId,
    chatId: data.chatId,
    message: data.fileData.fileName,
    senderUserId: localStorage.getItem("userId"),
    timestamp: data.timestamp,
    messageType: "FILE",
    fileName: data.fileData.fileName,
    fileUrl: data.fileData.fileUrl,
    fileExpiresAt: data.fileData.fileExpiresAt,
  };

  deps.setChatMessages((prevMessages) => [...prevMessages, finalFileMessage]);

  const currentUserId = localStorage.getItem("userId");
  deps.setChats((prevChats) =>
    prevChats.map((chat) =>
      applyFileMessageToChat(chat, data, currentUserId),
    ),
  );
  deps.setIsFileUploading(false);
};

const handleIncomingFileDisplay = async (data, deps) => {
  deps.setChatMessages((prevMessages) => [...prevMessages, data]);

  const readContext = getReadContext(deps, data);
  deps.setChats((prevChats) =>
    prevChats.map((chat) =>
      applyTextMessageToChat(
        chat,
        data,
        readContext.currentUserId,
        readContext.shouldMarkSeen,
      ),
    ),
  );

  maybePersistReadStatus(deps, data, readContext);
};

/** Handles file messages (presigned upload confirmation or display payload). */
export const handleFileMessage = async (data, deps) => {
  logger.info("File message received:", { data });

  if (data.fileData?.presignedUrl) {
    logger.info("File upload confirmation received:", { data });
    await handleFilePresignedUpload(data, deps);
  } else {
    await handleIncomingFileDisplay(data, deps);
  }
};

/** Backend connectivity test acknowledgement. */
export const handleTestConfirmation = (data) => {
  logger.info("Backend test confirmation received:", { data });
};

/** Logs unknown message types for diagnostics. */
export const handleUnknownMessage = (data) => {
  logger.info("Unhandled WebSocket message type:", { type: data.type });
};

export const CHAT_WS_HANDLERS = {
  NEW_MESSAGE: handleNewMessage,
  FILE_MESSAGE: handleFileMessage,
  TEST_CONFIRMATION: handleTestConfirmation,
};

/**
 * Parses a WebSocket payload and dispatches to the matching handler.
 * @param {MessageEvent} event
 * @param {Object} deps - Handler dependencies (setters, refs, navigate)
 */
export const dispatchChatWebSocketMessage = async (event, deps) => {
  logger.info("WebSocket message received:", { data: event.data });

  let data;
  try {
    data = JSON.parse(event.data);
  } catch (error) {
    logger.error("Invalid chat WebSocket payload:", {
      err: sanitizeError(error),
    });
    return;
  }

  if (!data || typeof data.type !== "string") {
    return;
  }

  const handler = CHAT_WS_HANDLERS[data.type] ?? handleUnknownMessage;
  await handler(data, deps);
};
